/**
 * Client-side helper for filing UK-GDPR Article 15–21 requests
 * through the Supabase data-subject-request edge function.
 *
 * Lives in @4cm/core/auth so coach-app, parent-portal, and admin
 * surfaces all hit the same typed contract.
 *
 *   await fileDsrRequest({
 *     supabaseUrl: env.VITE_SUPABASE_URL,
 *     accessToken: session.access_token,
 *     anonKey: env.VITE_SUPABASE_ANON_KEY,
 *     request: { type: 'restriction', restore: false },
 *   });
 *
 * Wave 2.1 — function call only; UI lives in apps/web2/src/pages/Settings.tsx.
 * Wave 2.2 will add data-subject-export + data-subject-delete edge
 * functions and wire them as auto-fulfilment paths from this helper.
 */

export const DSR_REQUEST_TYPES = [
  'access',
  'rectification',
  'erasure',
  'restriction',
  'portability',
  'object',
] as const;

export type DsrRequestType = (typeof DSR_REQUEST_TYPES)[number];

/**
 * LIA ids the user can object to. Mirrors lawful-basis-register.md.
 * Add new ids here AND in the edge function's ALLOWED_LIA_IDS set.
 */
export const LIA_IDS = ['lia_001', 'lia_002', 'lia_003', 'lia_004'] as const;
export type LiaId = (typeof LIA_IDS)[number];

export const LIA_DESCRIPTIONS: Record<LiaId, string> = {
  lia_001: 'Coach rating of player (legitimate interests basis)',
  lia_002: 'AI development analysis (legitimate interests basis)',
  lia_003: 'Server logs / pseudonymised observability (legitimate interests basis)',
  lia_004: 'Soft opt-in marketing emails (legitimate interests basis)',
};

export type DsrRequest =
  | { type: 'access'; notes?: string }
  | { type: 'rectification'; notes?: string }
  | { type: 'erasure'; notes?: string }
  | { type: 'portability'; notes?: string }
  | { type: 'restriction'; restore?: boolean; notes?: string }
  | { type: 'object'; liaIds: LiaId[]; notes?: string };

export interface FileDsrArgs {
  supabaseUrl: string;
  /** User session access token — required so the edge function can identify the user. */
  accessToken: string;
  /** Public anon key for the apikey header. */
  anonKey: string;
  request: DsrRequest;
  /** Wave 3.5 — parent-on-behalf-of-child. When set, the request is filed
   *  for this user (the child) with the caller as actor. Caller must
   *  have a parent_children link to the id. */
  onBehalfOfUserId?: string;
  /** Optional fetch override for tests. */
  fetchImpl?: typeof fetch;
}

export interface DsrResult {
  ok: boolean;
  dsrId?: string;
  requestType?: DsrRequestType;
  requestedAt?: string;
  dueAt?: string;
  sideEffect?: Record<string, unknown> | null;
  error?: string;
  status?: number;
}

export async function fileDsrRequest(args: FileDsrArgs): Promise<DsrResult> {
  const { supabaseUrl, accessToken, anonKey, request } = args;
  const fetchImpl = args.fetchImpl ?? fetch;

  if (!supabaseUrl || !accessToken || !anonKey) {
    return { ok: false, error: 'supabase env / session not configured' };
  }

  // Translate the discriminated-union DsrRequest into the edge
  // function's flat body shape.
  const body: Record<string, unknown> = {
    request_type: request.type,
    notes: request.notes,
  };
  if (request.type === 'restriction') {
    body.restore = request.restore === true;
  }
  if (request.type === 'object') {
    body.lia_ids = request.liaIds;
  }
  if (args.onBehalfOfUserId) {
    body.on_behalf_of_user_id = args.onBehalfOfUserId;
  }

  const url = `${supabaseUrl.replace(/\/$/, '')}/functions/v1/data-subject-request`;
  const resp = await fetchImpl(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      apikey: anonKey,
    },
    body: JSON.stringify(body),
  });

  let parsed: Record<string, unknown> | null = null;
  try {
    parsed = (await resp.json()) as Record<string, unknown>;
  } catch {
    /* ignore */
  }

  if (!resp.ok) {
    return {
      ok: false,
      status: resp.status,
      error:
        (parsed && typeof parsed.error === 'string' && parsed.error) ||
        `dsr request failed (${resp.status})`,
    };
  }

  return {
    ok: true,
    dsrId: typeof parsed?.dsr_id === 'string' ? parsed.dsr_id : undefined,
    requestType: typeof parsed?.request_type === 'string' ? (parsed.request_type as DsrRequestType) : undefined,
    requestedAt: typeof parsed?.requested_at === 'string' ? parsed.requested_at : undefined,
    dueAt: typeof parsed?.due_at === 'string' ? parsed.due_at : undefined,
    sideEffect: (parsed?.side_effect as Record<string, unknown> | null | undefined) ?? null,
  };
}

/**
 * Pretty label for a request type. UI uses this on buttons + the
 * request-history list.
 */
export const DSR_REQUEST_LABELS: Record<DsrRequestType, string> = {
  access: 'Download my data',
  rectification: 'Correct my data',
  erasure: 'Delete my account',
  restriction: 'Pause processing',
  portability: 'Export my data',
  object: 'Object to legitimate-interests processing',
};

// ---------------------------------------------------------------------------
// Auto-fulfilment helpers (Wave 2.2)
//
// These call the data-subject-export and data-subject-delete edge
// functions which both log the dsr_log row themselves AND execute the
// action — one round-trip from the UI.
// ---------------------------------------------------------------------------

export interface ExportMyDataArgs {
  supabaseUrl: string;
  accessToken: string;
  anonKey: string;
  /** 'access' (default) or 'portability' — same payload, different lawful-basis label. */
  requestType?: 'access' | 'portability';
  /** Wave 3.5 — parent-on-behalf-of-child. */
  onBehalfOfUserId?: string;
  fetchImpl?: typeof fetch;
}

export interface ExportMyDataResult {
  ok: boolean;
  /** dsr_log row id created for this fulfilment. */
  dsrId?: string;
  /** ISO timestamp the export was assembled. */
  fulfilledAt?: string;
  /** The full export blob. JSON-serialisable. */
  data?: Record<string, unknown>;
  error?: string;
  status?: number;
}

export async function exportMyData(args: ExportMyDataArgs): Promise<ExportMyDataResult> {
  const { supabaseUrl, accessToken, anonKey, requestType } = args;
  const fetchImpl = args.fetchImpl ?? fetch;

  if (!supabaseUrl || !accessToken || !anonKey) {
    return { ok: false, error: 'supabase env / session not configured' };
  }
  const url = `${supabaseUrl.replace(/\/$/, '')}/functions/v1/data-subject-export`;
  const resp = await fetchImpl(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      apikey: anonKey,
    },
    body: JSON.stringify({
      request_type: requestType ?? 'access',
      ...(args.onBehalfOfUserId ? { on_behalf_of_user_id: args.onBehalfOfUserId } : {}),
    }),
  });
  let parsed: Record<string, unknown> | null = null;
  try { parsed = (await resp.json()) as Record<string, unknown>; } catch { /* ignore */ }
  if (!resp.ok) {
    return {
      ok: false,
      status: resp.status,
      error:
        (parsed && typeof parsed.error === 'string' && parsed.error) ||
        `export failed (${resp.status})`,
    };
  }
  return {
    ok: true,
    dsrId: typeof parsed?.dsr_id === 'string' ? parsed.dsr_id : undefined,
    fulfilledAt: typeof parsed?.fulfilled_at === 'string' ? parsed.fulfilled_at : undefined,
    data: (parsed?.export as Record<string, unknown> | undefined) ?? undefined,
  };
}

export interface DeleteMyAccountArgs {
  supabaseUrl: string;
  accessToken: string;
  anonKey: string;
  /** Optional reason for the audit trail. */
  reason?: string;
  /** Wave 3.5 — parent-on-behalf-of-child erasure. */
  onBehalfOfUserId?: string;
  fetchImpl?: typeof fetch;
}

export interface DeleteMyAccountResult {
  ok: boolean;
  dsrId?: string;
  deletedAt?: string;
  /** Per-table row counts: ratings_anonymised_as_ratee, wellness_logs, etc. */
  summary?: Record<string, number>;
  /** Per-step errors during the cascade. ok may still be false even if some steps succeeded. */
  errors?: Array<{ step: string; message: string }>;
  error?: string;
  status?: number;
}

export async function deleteMyAccount(args: DeleteMyAccountArgs): Promise<DeleteMyAccountResult> {
  const { supabaseUrl, accessToken, anonKey, reason } = args;
  const fetchImpl = args.fetchImpl ?? fetch;

  if (!supabaseUrl || !accessToken || !anonKey) {
    return { ok: false, error: 'supabase env / session not configured' };
  }
  const url = `${supabaseUrl.replace(/\/$/, '')}/functions/v1/data-subject-delete`;
  const resp = await fetchImpl(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      apikey: anonKey,
    },
    body: JSON.stringify({
      confirm: 'CONFIRM',
      reason,
      ...(args.onBehalfOfUserId ? { on_behalf_of_user_id: args.onBehalfOfUserId } : {}),
    }),
  });
  let parsed: Record<string, unknown> | null = null;
  try { parsed = (await resp.json()) as Record<string, unknown>; } catch { /* ignore */ }
  if (!resp.ok) {
    return {
      ok: false,
      status: resp.status,
      error:
        (parsed && typeof parsed.error === 'string' && parsed.error) ||
        `delete failed (${resp.status})`,
    };
  }
  return {
    ok: parsed?.ok === true,
    dsrId: typeof parsed?.dsr_id === 'string' ? parsed.dsr_id : undefined,
    deletedAt: typeof parsed?.deleted_at === 'string' ? parsed.deleted_at : undefined,
    summary: (parsed?.summary as Record<string, number> | undefined) ?? undefined,
    errors: (parsed?.errors as Array<{ step: string; message: string }> | undefined) ?? undefined,
  };
}
